/**
 * compressImage — client-side image downscale & re-encode to JPEG.
 *
 * Why: phone photos routinely arrive at 8–15 MB (HEIC/large JPG). For field
 * documentation we don't need that resolution — a downscaled JPEG at modest
 * quality is visually indistinguishable, cuts cellular data ~95%, and makes
 * S3 uploads near-instant. Normalising to JPEG also means the server,
 * browsers, and Outlook/QuickBooks invoice previews all render attachments
 * consistently (HEIC isn't supported by every viewer).
 *
 * Hard byte budget (Jun 2026 — Corey): every uploaded photo must be ≤5 MB.
 * The function now *guarantees* the output size or returns the smallest
 * achievable JPEG. It does that by:
 *   1. First pass: downscale to `maxDim` (2048 px) at `quality` (0.82) JPEG.
 *   2. If the result still overshoots `targetMaxBytes`, iteratively drop
 *      quality 0.82 → 0.7 → 0.55 → 0.4.
 *   3. If quality reduction alone isn't enough (very high-resolution
 *      scenes), step the longer edge down 1800 → 1600 → 1280 → 1024 and
 *      re-run the quality ladder.
 *   4. Returns whatever blob *first* fits the budget. Worst case (extremely
 *      detailed image that refuses to compress) it returns the smallest
 *      blob we produced — never larger than the source.
 *
 * Returns a Promise<File> — always safe to upload via FormData. Falls back
 * to the original file if anything in the pipeline throws, so we never
 * block the user from uploading.
 */
const DEFAULT_TARGET = 5 * 1024 * 1024; // 5 MB

export async function compressImage(
  file,
  {
    maxDim = 2048,
    quality = 0.82,
    mimeType = 'image/jpeg',
    targetMaxBytes = DEFAULT_TARGET,
  } = {},
) {
  if (!file || !(file instanceof File)) return file;
  // Already a JPEG comfortably under the target — no benefit to re-encode.
  if (
    file.size <= targetMaxBytes &&
    file.size < 800 * 1024 &&
    (file.type === 'image/jpeg' || file.name.toLowerCase().endsWith('.jpg'))
  ) {
    return file;
  }

  try {
    const dataUrl = await readFileAsDataURL(file);
    const img = await loadImage(dataUrl);
    const { naturalWidth: srcW, naturalHeight: srcH } = img;
    if (!srcW || !srcH) throw new Error('image decode produced 0×0 bitmap');

    // Quality + resolution ladder. We try the *largest* setting first so the
    // 99% case (small phone photo) gets the best fidelity, then back off
    // only as far as needed to satisfy `targetMaxBytes`.
    const qualityLadder = [quality, 0.7, 0.55, 0.4];
    const dimLadder = [maxDim, 1800, 1600, 1280, 1024];

    let bestBlob = null; // smallest blob seen so far — used as last resort
    let bestName = `${(file.name || 'photo').replace(/\.[^.]+$/, '')}.jpg`;

    for (const dim of dimLadder) {
      // Compute target canvas size honouring the dim cap. Skip rendering
      // if the source is already smaller than the cap to avoid pointless
      // upscaling on tiny screenshots.
      let w = srcW;
      let h = srcH;
      if (w > dim || h > dim) {
        const scale = dim / Math.max(w, h);
        w = Math.round(w * scale);
        h = Math.round(h * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      // White background so transparent PNGs don't go black on JPEG.
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);

      for (const q of qualityLadder) {
        const blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, mimeType, q),
        );
        if (!blob) continue;
        if (!bestBlob || blob.size < bestBlob.size) bestBlob = blob;
        if (blob.size <= targetMaxBytes) {
          return new File([blob], bestName, {
            type: mimeType,
            lastModified: Date.now(),
          });
        }
      }
    }

    // Couldn't get under the byte budget at any setting. Return the
    // *smallest* blob we produced — still smaller than the original in
    // every realistic scenario.
    if (bestBlob) {
      // If even the smallest version is still larger than the source
      // (theoretically possible for tiny PNGs), keep the source.
      if (bestBlob.size >= file.size) return file;
      return new File([bestBlob], bestName, {
        type: mimeType,
        lastModified: Date.now(),
      });
    }
    return file;
  } catch (err) {
    // Never block uploads — fall back to the original file untouched.
    // This typically only happens on Android Chrome with a HEIC photo
    // (no native decoder), where the backend will store it as-is.
    console.warn('compressImage fallback to original file:', err?.message || err);
    return file;
  }
}

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image load failed (unsupported format?)'));
    img.src = src;
  });
}

/** True if the File looks like an image we know how to compress in-browser. */
export function isCompressibleImage(file) {
  if (!file || !(file instanceof File)) return false;
  if (file.type && file.type.startsWith('image/')) return true;
  const dot = file.name.lastIndexOf('.');
  if (dot < 0) return false;
  const ext = file.name.slice(dot).toLowerCase();
  return ['.jpg', '.jpeg', '.png', '.heic', '.heif', '.webp', '.gif'].includes(ext);
}
